const api = (() => {
  const { REACT_APP_PROTOCOL, REACT_APP_HOST, REACT_APP_PORT } = process.env;
  const BASE_URL = `${REACT_APP_PROTOCOL}://${REACT_APP_HOST}:${REACT_APP_PORT}`;

  function putAccessToken(token) {
    localStorage.setItem('accessToken', token);
  }

  function getAccessToken() {
    return localStorage.getItem('accessToken');
  }

  function putRefreshToken(token) {
    return localStorage.setItem('refreshToken', token);
  }

  function getRefreshToken() {
    return localStorage.getItem('refreshToken');
  }

  function deleteToken() {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  }

  async function _fetchWithAuth(url, options = {}) {
    return fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        Authorization: `Bearer ${getAccessToken()}`,
      },
    });
  }

  async function register({ name, email, password }) {
    const response = await fetch(`${BASE_URL}/users`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        name,
        email,
        password,
      }),
    });

    const responseJson = await response.json();
    const { status, message } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return { status };
  }

  async function login({ email, password }) {
    const response = await fetch(`${BASE_URL}/authentications`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        password,
      }),
    });

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function refreshToken() {
    const response = await fetch(`${BASE_URL}/authentications`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        refreshToken: `${getRefreshToken()}`,
      }),
    });

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }
    putAccessToken(data.accessToken);
  }

  async function getOwnProfile() {
    const response = await _fetchWithAuth(`${BASE_URL}/me/profile`);

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }
    return data;
  }

  async function getBatch(params = {}) {
    let url = `${BASE_URL}/batch`;
    const query = new URLSearchParams();
    if (params.programId) {
      query.append('programId', params.programId);
    }
    const queryString = query.toString();
    if (queryString) {
      url += `?${queryString}`;
    }

    const response = await _fetchWithAuth(url);

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function addBatch({
    programId,
    batch,
    startDate,
    endDate,
    statusOffered,
    isOpen,
  }) {
    const response = await _fetchWithAuth(`${BASE_URL}/batch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        programId,
        batch,
        startDate,
        endDate,
        statusOffered,
        isOpen,
      }),
    });

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function getBatchById({ id }) {
    const response = await _fetchWithAuth(`${BASE_URL}/batch/${id}`);

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function updateBatch({
    batchId,
    batch,
    startDate,
    endDate,
    statusOffered,
  }) {
    const response = await _fetchWithAuth(`${BASE_URL}/batch/${batchId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        batch,
        startDate,
        endDate,
        statusOffered,
      }),
    });

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function changeBatchOpen({ batchId }) {
    const response = await _fetchWithAuth(
      `${BASE_URL}/batch/${batchId}/is-open`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function changeBatchStatus({ batchId }) {
    const response = await _fetchWithAuth(
      `${BASE_URL}/batch/${batchId}/status`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function getBatchOptions() {
    const response = await _fetchWithAuth(`${BASE_URL}/batch/options`);

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function deleteBatch({ batchId }) {
    const response = await _fetchWithAuth(`${BASE_URL}/batch/${batchId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function addSectionsProgram({
    programId,
    title,
    descriptions,
    sequence,
  }) {
    const response = await _fetchWithAuth(`${BASE_URL}/sections/program`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        programId,
        title,
        descriptions,
        sequence,
      }),
    });

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function getSectionsProgram() {
    const response = await _fetchWithAuth(`${BASE_URL}/sections/program`);

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function getSectionsByProgram({ programId }) {
    const response = await _fetchWithAuth(
      `${BASE_URL}/sections/program/${programId}`
    );

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function updateSectionsProgram({
    sectionId,
    title,
    description,
    sequence,
  }) {
    const response = await _fetchWithAuth(
      `${BASE_URL}/sections/program/${sectionId}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title,
          description,
          sequence,
        }),
      }
    );

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function deleteSections({ sectionId }) {
    const response = await _fetchWithAuth(
      `${BASE_URL}/sections/program/${sectionId}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function addSectionsBatch({
    batchId,
    programSectionsId,
    startDate,
    endDate,
  }) {
    const response = await _fetchWithAuth(`${BASE_URL}/sections/batch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        batchId,
        programSectionsId,
        startDate,
        endDate,
      }),
    });

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function updateSectionsBatch({
    sectionId,
    programSectionsId,
    startDate,
    endDate,
  }) {
    const response = await _fetchWithAuth(
      `${BASE_URL}/sections/batch/${sectionId}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title,
          programSectionsId,
          startDate,
          endDate,
        }),
      }
    );

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function deleteSectionsBatch({ sectionId }) {
    const response = await _fetchWithAuth(
      `${BASE_URL}/sections/batch/${sectionId}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function addEmployee({
    nip,
    noNik,
    firstName,
    lastName,
    email,
    password,
    dateStartWork,
    dateEndWork,
    fileCv,
    linkedIn,
    systemRole,
  }) {
    const response = await _fetchWithAuth(`${BASE_URL}/office/employees`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        nip,
        noNik,
        firstName,
        lastName,
        email,
        password,
        dateStartWork,
        dateEndWork,
        fileCv,
        linkedIn,
        systemRole,
      }),
    });

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function updateEmployee({
    id,
    nip,
    noNik,
    firstName,
    lastName,
    dateStartWork,
    dateEndWork,
    fileCv,
    linkedIn,
    systemRole,
  }) {
    const response = await _fetchWithAuth(`${BASE_URL}/office/employees/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        nip,
        noNik,
        firstName,
        lastName,
        dateStartWork,
        dateEndWork,
        fileCv,
        linkedIn,
        systemRole,
      }),
    });

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function resetEmployeePassword({ id, newPassword }) {
    const response = await _fetchWithAuth(`${BASE_URL}/office/employees/${id}/password`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ newPassword }),
    });

    const responseJson = await response.json();
    const { status, message } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return responseJson;
  }

  async function deleteEmployee({ id }) {
    const response = await _fetchWithAuth(`${BASE_URL}/office/employees/${id}`, {
      method: 'DELETE',
    });

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function getEmployees({ pageSize, page }) {
    const response = await _fetchWithAuth(
      `${BASE_URL}/office/employees?pageSize=${pageSize}&page=${page}`
    );

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function getEmployeeDetail({ id }) {
    const response = await _fetchWithAuth(`${BASE_URL}/office/employees/${id}`);

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function attendProgramToEmployee({ programId, employeeId }) {
    const response = await _fetchWithAuth(
      `${BASE_URL}/office/employees/attend-program`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          programId,
          employeeId,
        }),
      }
    );

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  // RBAC
  async function getPermissionCatalog({ domain } = {}) {
    const url = domain
      ? `${BASE_URL}/office/permissions?domain=${domain}`
      : `${BASE_URL}/office/permissions`;
    const response = await _fetchWithAuth(url);

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function getRoles({ domain } = {}) {
    const url = domain ? `${BASE_URL}/office/roles?domain=${domain}` : `${BASE_URL}/office/roles`;
    const response = await _fetchWithAuth(url);

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function getRoleDetail({ id }) {
    const response = await _fetchWithAuth(`${BASE_URL}/office/roles/${id}`);

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function addRole({
    code, domain, name, description, programId, permissionIds,
  }) {
    const response = await _fetchWithAuth(`${BASE_URL}/office/roles`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        code, domain, name, description, programId, permissionIds,
      }),
    });

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function updateRole({
    id, name, description, permissionIds,
  }) {
    const response = await _fetchWithAuth(`${BASE_URL}/office/roles/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ name, description, permissionIds }),
    });

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function deleteRole({ id }) {
    const response = await _fetchWithAuth(`${BASE_URL}/office/roles/${id}`, {
      method: 'DELETE',
    });

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function getEmployeeAccess({ id }) {
    const response = await _fetchWithAuth(`${BASE_URL}/office/employees/${id}/access`);

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function updateEmployeeAccess({ id, roles, overrides }) {
    const response = await _fetchWithAuth(`${BASE_URL}/office/employees/${id}/access`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ roles, overrides }),
    });

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function removeProgramFromEmployee({ employeeId, programId }) {
    const response = await _fetchWithAuth(
      `${BASE_URL}/office/employees/${employeeId}/program/${programId}`,
      {
        method: 'DELETE',
      }
    );

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function assignDivisionToEmployee({ employeeId, divisionId }) {
    const response = await _fetchWithAuth(
      `${BASE_URL}/office/employees/${employeeId}/division`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          divisionId,
        }),
      }
    );

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function removeDivisionFromEmployee({ employeeId, divisionId }) {
    const response = await _fetchWithAuth(
      `${BASE_URL}/office/employees/${employeeId}/division/${divisionId}`,
      {
        method: 'DELETE',
      }
    );

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  // PROGRAM
  async function getAllFirstThierProgram() {
    const response = await _fetchWithAuth(`${BASE_URL}/divisions`);

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function getFirstThierProgramById({ id }) {
    const response = await _fetchWithAuth(`${BASE_URL}/divisions/${id}`);

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function getFirstTierPrograms() {
    const response = await _fetchWithAuth(`${BASE_URL}/divisions`);
    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function addFirstTierProgram({ employeeId, name, description, status }) {
    const response = await _fetchWithAuth(`${BASE_URL}/divisions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ employeeId, name, description, status }),
    });

    const responseJson = await response.json();
    const { status: respStatus, message, data } = responseJson;

    if (respStatus !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function updateFirstTierProgram({ id, employeeId, name, description, status }) {
    const response = await _fetchWithAuth(`${BASE_URL}/divisions/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ employeeId, name, description, status }),
    });

    const responseJson = await response.json();
    const { status: respStatus, message, data } = responseJson;

    if (respStatus !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function deleteFirstTierProgram({ id }) {
    const response = await _fetchWithAuth(`${BASE_URL}/divisions/${id}`, {
      method: 'DELETE',
    });

    const responseJson = await response.json();
    const { status: respStatus, message, data } = responseJson;

    if (respStatus !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function getAllSubDivision() {
    const response = await _fetchWithAuth(`${BASE_URL}/divisions/sub`);

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function getAllSubDivisionOptions({ programId }) {
    const response = await _fetchWithAuth(
      `${BASE_URL}/divisions/sub-options?programId=${programId}`
    );

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function getSubDivisionById({ id }) {
    const response = await _fetchWithAuth(`${BASE_URL}/divisions/sub/${id}`);

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  // PROGRAM
  async function addProgram({ programId, programName, descriptions }) {
    const response = await _fetchWithAuth(`${BASE_URL}/program`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        programId,
        programName,
        descriptions,
      }),
    });

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function getAllProgram() {
    const response = await _fetchWithAuth(`${BASE_URL}/program`);

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function getAllProgramOptions() {
    const response = await _fetchWithAuth(`${BASE_URL}/program-options`);

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function getProgramById({ id }) {
    const response = await _fetchWithAuth(`${BASE_URL}/program/${id}`);

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  // Analytics

  async function analyticsUsersTotal() {
    const response = await _fetchWithAuth(`${BASE_URL}/analytics/users-total`);

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function analyticsUsersByUniversities() {
    const response = await _fetchWithAuth(
      `${BASE_URL}/analytics/users-universities`
    );

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function analyticsUsersByMajor() {
    const response = await _fetchWithAuth(`${BASE_URL}/analytics/users-major`);

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function analyticsUsersByProvincies() {
    const response = await _fetchWithAuth(
      `${BASE_URL}/analytics/users-domicilies/provincies`
    );

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function analyticsUsersByRegencies() {
    const response = await _fetchWithAuth(
      `${BASE_URL}/analytics/users-domicilies/regencies`
    );

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function analyticsUsersByInstitutions() {
    const response = await _fetchWithAuth(
      `${BASE_URL}/analytics/users-institutions`
    );

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function analyticsUsersByDivision() {
    const response = await _fetchWithAuth(
      `${BASE_URL}/analytics/users-division`
    );

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function analyticsUsersByGender() {
    const response = await _fetchWithAuth(`${BASE_URL}/analytics/users-gender`);

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function analyticsUsersByGrowth() {
    const response = await _fetchWithAuth(`${BASE_URL}/analytics/users-growth`);

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function analyticsUsersByFollowers() {
    const response = await _fetchWithAuth(
      `${BASE_URL}/analytics/users-followers`
    );

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function analyticsDashboardManagement({ batchId, programId }) {
    const params = new URLSearchParams({ batchId });
    if (programId) params.set('programId', programId);

    const response = await _fetchWithAuth(
      `${BASE_URL}/analytics/dashboard-management?${params.toString()}`
    );

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function analyticsProgramFunnel({ batchId, programId }) {
    const params = new URLSearchParams({ batchId });
    if (programId) params.set('programId', programId);

    const response = await _fetchWithAuth(
      `${BASE_URL}/analytics/program-funnel?${params.toString()}`
    );

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function analyticsCohortOptions() {
    const response = await _fetchWithAuth(`${BASE_URL}/analytics/cohort-options`);

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function analyticsClpReach() {
    const response = await _fetchWithAuth(`${BASE_URL}/analytics/clp-reach`);

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function getApiKeys() {
    const response = await _fetchWithAuth(`${BASE_URL}/office/api-keys`);

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function createApiKey({ clientName, expiresInDays }) {
    const response = await _fetchWithAuth(`${BASE_URL}/office/api-keys`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clientName, expiresInDays }),
    });

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function revokeApiKey({ id }) {
    const response = await _fetchWithAuth(`${BASE_URL}/office/api-keys/${id}`, {
      method: 'DELETE',
    });

    const responseJson = await response.json();
    const { status, message } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }
  }

  async function getExecutiveTargets({ year } = {}) {
    const url = year
      ? `${BASE_URL}/office/executive-targets?year=${year}`
      : `${BASE_URL}/office/executive-targets`;
    const response = await _fetchWithAuth(url);

    const responseJson = await response.json();
    const { status, message, data } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }

    return data;
  }

  async function saveExecutiveTarget({
    year, metricCode, targetValue, note,
  }) {
    const response = await _fetchWithAuth(`${BASE_URL}/office/executive-targets`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        year, metricCode, targetValue, note,
      }),
    });

    const responseJson = await response.json();
    const { status, message } = responseJson;

    if (status !== 'success') {
      throw new Error(message);
    }
  }

  return {
    putAccessToken,
    getAccessToken,
    putRefreshToken,
    getRefreshToken,
    refreshToken,
    deleteToken,
    register,
    login,
    getOwnProfile,
    // BATCH
    addBatch,
    getBatch,
    getBatchOptions,
    getBatchById,
    updateBatch,
    changeBatchOpen,
    changeBatchStatus,
    deleteBatch,
    // SECTIONS
    addSectionsProgram,
    getSectionsProgram,
    getSectionsByProgram,
    updateSectionsProgram,
    deleteSections,
    addSectionsBatch,
    updateSectionsBatch,
    deleteSectionsBatch,
    addEmployee,
    updateEmployee,
    resetEmployeePassword,
    getEmployees,
    getEmployeeDetail,
    deleteEmployee,
    attendProgramToEmployee,
    removeProgramFromEmployee,
    assignDivisionToEmployee,
    removeDivisionFromEmployee,
    // RBAC
    getPermissionCatalog,
    getRoles,
    getRoleDetail,
    addRole,
    updateRole,
    deleteRole,
    getEmployeeAccess,
    updateEmployeeAccess,
    // PROGRAM
    getAllFirstThierProgram,
    getFirstThierProgramById,
    getFirstTierPrograms,
    addFirstTierProgram,
    updateFirstTierProgram,
    deleteFirstTierProgram,
    getAllSubDivision,
    getAllSubDivisionOptions,
    getSubDivisionById,
    addProgram,
    getAllProgram,
    getAllProgramOptions,
    getProgramById,
    // ANALYTICS
    analyticsUsersTotal,
    analyticsUsersByUniversities,
    analyticsUsersByMajor,
    analyticsUsersByProvincies,
    analyticsUsersByRegencies,
    analyticsUsersByInstitutions,
    analyticsUsersByDivision,
    analyticsUsersByGender,
    analyticsUsersByGrowth,
    analyticsUsersByFollowers,
    analyticsDashboardManagement,
    analyticsProgramFunnel,
    analyticsCohortOptions,
    analyticsClpReach,
    // EXECUTIVE API
    getApiKeys,
    createApiKey,
    revokeApiKey,
    getExecutiveTargets,
    saveExecutiveTarget,
  };
})();

export default api;
